import { useCallback, useState, useEffect } from 'react';

// ✅ INTERFACE PARA ERRORES DE API
export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  details?: any;
}

// ✅ INTERFACE PARA EL HOOK
export interface UseApiErrorHandlerReturn {
  error: ApiError | null;
  setError: (error: ApiError | null) => void;
  clearError: () => void;
  handleApiError: (error: any) => void;
  isError: boolean;
}

// ✅ FUNCIÓN PARA NORMALIZAR ERRORES
const normalizeError = (error: any): ApiError => {
  if (error?.response?.data) {
    // Error de Axios con respuesta del servidor
    return {
      message:
        error.response.data.message ||
        error.response.data.error ||
        'Error del servidor',
      code: error.response.data.code,
      status: error.response.status,
      details: error.response.data,
    };
  }

  if (error?.message) {
    // Error estándar de JavaScript
    return {
      message: error.message,
      code: error.code,
      details: error,
    };
  }

  if (typeof error === 'string') {
    // Error como string
    return {
      message: error,
    };
  }

  // Error desconocido
  return {
    message: 'Error desconocido',
    details: error,
  };
};

// ✅ HOOK PARA MANEJO DE ERRORES DE API
export const useApiErrorHandler = (): UseApiErrorHandlerReturn => {
  const [error, setError] = useState<ApiError | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleApiError = useCallback((error: any) => {
    const normalizedError = normalizeError(error);
    setError(normalizedError);

    // ✅ LOGGING DEL ERROR
    console.error('API Error:', normalizedError);

    // ✅ AQUÍ SE PUEDE INTEGRAR CON SERVICIOS DE MONITOREO
    // - Sentry
    // - LogRocket
    // - Bugsnag
    if (process.env.REACT_APP_ERROR_LOGGING_URL) {
      const fetchPromise = fetch(process.env.REACT_APP_ERROR_LOGGING_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'api_error',
          error: normalizedError,
          timestamp: new Date().toISOString(),
          url: window.location.href,
        }),
      });
      // ✅ MANEJO SEGURO DEL CATCH
      if (fetchPromise && typeof fetchPromise.catch === 'function') {
        fetchPromise.catch(console.error);
      }
    }
  }, []);

  return {
    error,
    setError,
    clearError,
    handleApiError,
    isError: !!error,
  };
};

// ✅ HOOK PARA MANEJO DE ERRORES CON AUTO-CLEAR
export const useAutoClearApiError = (
  delay = 5000
): UseApiErrorHandlerReturn => {
  const { error, setError, clearError, handleApiError, isError } =
    useApiErrorHandler();

  // ✅ AUTO-CLEAR DEL ERROR DESPUÉS DE UN DELAY
  useEffect(() => {
    if (error && delay > 0) {
      const timer = setTimeout(() => {
        clearError();
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [error, delay, clearError]);

  return {
    error,
    setError,
    clearError,
    handleApiError,
    isError,
  };
};
